import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppShell, { SectionCard, StatCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ArrowLeft, Buildings, Users, Stack, Calendar, PaperPlaneTilt, Copy, CheckCircle, Warning, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CompanyDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);

  const load = async () => {
    try {
      const [comp, adm] = await Promise.all([
        api.get(`/companies/${id}`),
        api.get(`/companies/${id}/admin`).catch(() => ({ data: { admin: null } })),
      ]);
      setC(comp.data);
      setAdmin(adm.data?.admin || null);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load company");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const resend = async ({ copyAfter = false } = {}) => {
    setResending(true);
    try {
      // When the operator just wants to grab the link, skip sending an email
      // so the recipient's inbox isn't nudged again unnecessarily.
      const url = `/companies/${id}/resend-invite${copyAfter ? "?send_email=false" : ""}`;
      const { data } = await api.post(url);
      if (copyAfter && data.setup_url && navigator.clipboard) {
        await navigator.clipboard.writeText(data.setup_url);
        toast.success(`Setup link copied — valid ${data.expires_in_hours}h. Share with ${data.email} via any channel.`);
      } else {
        toast.success(data.delivered
          ? `Fresh invite emailed to ${data.email} (link valid ${data.expires_in_hours}h).`
          : `Invite re-issued for ${data.email} (email delivery may be delayed; link valid ${data.expires_in_hours}h).`);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not resend invite");
    } finally { setResending(false); }
  };

  return (
    <AppShell title={c?.name || "Company"}>
      <div className="space-y-5" data-testid="company-detail">
        <Link to="/app/companies" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900" data-testid="company-back-link">
          <ArrowLeft size={12}/> Back to companies
        </Link>

        {loading ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : !c ? (
          <div className="text-sm text-zinc-500">Company not found.</div>
        ) : (
          <>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-semibold tracking-tight">{c.name}</h2>
                  <Badge variant="outline" className="uppercase text-[10px]">{c.plan}</Badge>
                  <span className="text-[10px] uppercase tracking-wider bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full">{c.status}</span>
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  {c.industry || "—"}{c.country ? ` · ${c.country}` : ""}
                  {c.id ? <> · <span className="font-mono">{c.id.slice(0, 12)}</span></> : null}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <StatCard label="Employees" value={c.employee_count ?? 0} icon={Users}/>
              <StatCard label="Plan" value={c.plan || "—"} icon={Stack}/>
              <StatCard label="Status" value={c.status || "—"} icon={Buildings}/>
              <StatCard label="Onboarded" value={(c.created_at || "").slice(0, 10) || "—"} icon={Calendar}/>
            </div>

            {/* Company admin contact + invite controls */}
            <SectionCard
              title="Company admin"
              subtitle="The user who received the onboarding invite and acts as the primary contact for this tenant."
              testid="company-admin-card"
            >
              {admin ? (
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="space-y-1.5 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <EnvelopeSimple size={14} className="text-zinc-400"/>
                      <span className="font-mono text-sm text-zinc-900" data-testid="admin-email">{admin.email}</span>
                      <AdminStateBadge admin={admin} />
                    </div>
                    <div className="text-xs text-zinc-500">
                      Name: <span className="text-zinc-700">{admin.name || "—"}</span>
                      <span className="mx-2 text-zinc-300">·</span>
                      Created: <span className="text-zinc-700">{(admin.created_at || "").slice(0, 10) || "—"}</span>
                    </div>
                    {!admin.email_verified && admin.has_pending_invite && (
                      <p className="text-[11px] text-amber-700 leading-snug max-w-md mt-1">
                        Hasn't signed in yet. They received a magic-link invite — if missed, resend it below or copy the link to share via WhatsApp/Slack.
                      </p>
                    )}
                    {admin.email_verified && (
                      <p className="text-[11px] text-emerald-700 leading-snug max-w-md mt-1">
                        Admin is active and using the platform. No invite to resend.
                      </p>
                    )}
                  </div>
                  {/* Only surface invite actions when there's actually an
                      outstanding invite to act on. For active admins these
                      buttons would be confusing — they'd reset the admin
                      back into "must set password" mode unnecessarily. */}
                  {!admin.email_verified && admin.has_pending_invite && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={resending}
                        onClick={() => resend({ copyAfter: false })}
                        data-testid="detail-resend-invite"
                      >
                        <PaperPlaneTilt size={12} className="mr-1.5"/>
                        {resending ? "Sending…" : "Resend invite email"}
                      </Button>
                      <Button
                        size="sm"
                        className="bg-zinc-950 hover:bg-zinc-800"
                        disabled={resending}
                        onClick={() => resend({ copyAfter: true })}
                        data-testid="detail-copy-setup-link"
                      >
                        <Copy size={12} className="mr-1.5"/>
                        Copy setup link
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-zinc-500" data-testid="admin-missing">
                  No company admin user is linked to this tenant. Onboard one from <Link to="/app/companies" className="underline">Companies</Link>.
                </div>
              )}
            </SectionCard>

            <SectionCard title="Tenant configuration" subtitle="Read-only summary. Module entitlements & seat caps are managed under Modules." testid="company-cfg">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <Field label="Company ID" value={<span className="font-mono text-xs">{c.id}</span>}/>
                <Field label="Reseller ID" value={c.reseller_id ? <span className="font-mono text-xs">{c.reseller_id}</span> : "Direct"}/>
                <Field label="Industry" value={c.industry || "—"}/>
                <Field label="Plan" value={c.plan || "—"}/>
                <Field label="Country" value={c.country || "—"}/>
                <Field label="Status" value={c.status || "—"}/>
                <Field label="Created" value={(c.created_at || "").slice(0, 10) || "—"}/>
                <Field label="Updated" value={(c.updated_at || "").slice(0, 10) || "—"}/>
              </dl>
            </SectionCard>

            <SectionCard title="Quick actions" subtitle="Open this tenant's data inside the platform shell." testid="company-actions">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to="/app/modules"><Button size="sm" variant="outline" data-testid="company-modules-link">Manage modules</Button></Link>
                <Link to="/app/audit-log"><Button size="sm" variant="outline" data-testid="company-audit-link">Audit log</Button></Link>
                <Link to="/app/employees"><Button size="sm" variant="outline" data-testid="company-employees-link">View employees</Button></Link>
              </div>
            </SectionCard>
          </>
        )}
      </div>
    </AppShell>
  );
}

function AdminStateBadge({ admin }) {
  if (admin.email_verified) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full" data-testid="admin-state-verified">
        <CheckCircle size={11} weight="fill"/> Signed in
      </span>
    );
  }
  if (admin.has_pending_invite) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full" data-testid="admin-state-pending">
        <Warning size={11} weight="fill"/> Invite pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-zinc-100 border border-zinc-200 text-zinc-700 px-2 py-0.5 rounded-full" data-testid="admin-state-stale">
      <Warning size={11}/> Not signed in
    </span>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</dt>
      <dd className="text-sm text-zinc-900 mt-0.5">{value}</dd>
    </div>
  );
}
